import type { MediaAction, MediaBadge, MediaCard } from '@/protocol/media';

/** The action a select should take. The agent marks one; otherwise the first is it. */
export function primaryAction(card: MediaCard): MediaAction | null {
  const actions = card.actions;
  if (!actions?.length) return null;
  return actions.find((a) => a.primary) ?? actions[0];
}

export function badgeOfKind(card: MediaCard, kind: string): MediaBadge | null {
  return card.badges?.find((b) => b.kind === kind) ?? null;
}

/** Badges that aren't already shown in a dedicated slot on the tile. */
export function looseBadges(card: MediaCard): MediaBadge[] {
  return (card.badges ?? []).filter((b) => b.kind !== 'live' && b.kind !== 'entitlement');
}

export function isLive(card: MediaCard): boolean {
  return card.contentType === 'live' || !!card.airing?.isLive;
}

/**
 * When the airing is, in the shortest form that is still unambiguous. A negative
 * `startsInMinutes` means it is already under way.
 */
export function airingLine(card: MediaCard): string | null {
  const airing = card.airing;
  if (!airing) return null;

  const parts: string[] = [];
  const startsIn = airing.startsInMinutes;

  if (airing.isLive || (startsIn != null && startsIn < 0)) {
    parts.push('On now');
  } else if (startsIn != null) {
    parts.push(startsIn < 1 ? 'Starting now' : `Starts in ${formatDuration(startsIn)}`);
  } else if (airing.startTime) {
    const at = formatClockTime(airing.startTime);
    if (at) parts.push(at);
  }

  if (airing.channelNumber != null) parts.push(`Ch ${airing.channelNumber}`);

  return parts.length ? parts.join(' · ') : null;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

export function formatClockTime(iso: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Stands in for missing art: up to two initials from the title. */
export function monogram(card: MediaCard): string {
  return card.title
    .split(/\s+/)
    .filter((word) => /[a-z0-9]/i.test(word))
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

/** What the client would hand the action off to, shown because performing it is the device's job. */
export function actionTarget(action: MediaAction): string | null {
  if (action.uri) return action.uri;
  const params = action.params;
  if (!params) return null;
  const values = Object.values(params).filter((v) => v != null && v !== '');
  return values.length ? values.map(String).join(' · ') : null;
}
